import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import LoadingSpinner from "./LoadingSpinner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  RefreshCw,
  Download,
  DollarSign,
  Users,
  MousePointerClick,
  TrendingDown,
  Globe,
  Monitor,
  Smartphone,
  ExternalLink,
  Trash2,
  Plus,
  Share2,
  Rocket,
  Search as SearchIcon,
  ExternalLink as ReferralIcon,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtMs = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;
const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const RANGE_OPTIONS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

async function fetchAdmin(path, params = {}) {
  const token = localStorage.getItem("adminToken");
  const qs = new URLSearchParams(params).toString();
  const url = `${BACKEND}/api/admin/analytics/${path}${qs ? `?${qs}` : ""}`;
  try {
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  } catch (err) {
    // Stale/rotated admin token: clear it and send the admin back to login
    if (err.response?.status === 401) {
      localStorage.removeItem("adminToken");
      window.location.reload();
    }
    throw err;
  }
}

function KpiTile({ label, value, sub, icon: Icon, tone = "default", testId }) {
  const toneMap = {
    default: "text-slate-900",
    warn: "text-amber-600",
    danger: "text-rose-600",
    good: "text-emerald-600",
    accent: "text-blue-600",
  };
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
      </div>
      <div className={`mt-2 text-3xl font-semibold ${toneMap[tone]}`}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

function SectionCard({ title, right, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {right}
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function ViewsLineChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-72 flex items-center justify-center text-sm text-slate-400 border border-dashed border-slate-700 rounded">
        No visitor data in this range yet.
      </div>
    );
  }
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
          <XAxis
            dataKey="label"
            stroke="#94a3b8"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: "#334155" }}
            minTickGap={24}
          />
          <YAxis
            stroke="#94a3b8"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          <Tooltip
            contentStyle={{
              background: "#0f172a",
              border: "1px solid #334155",
              borderRadius: 8,
              color: "#e2e8f0",
              fontSize: 12,
            }}
            labelStyle={{ color: "#94a3b8", fontSize: 11 }}
            cursor={{ stroke: "#f59e0b", strokeDasharray: "3 3" }}
          />
          <Line
            type="monotone"
            dataKey="value"
            name="Unique visitors"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={{ r: 3, fill: "#f59e0b", stroke: "#0f172a", strokeWidth: 1.5 }}
            activeDot={{ r: 5, fill: "#f59e0b" }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const BUCKET_STYLE = {
  Social:   { icon: Share2,       color: "#a78bfa", label: "Social",   sub: "Facebook, Instagram, X, LinkedIn, TikTok…" },
  Direct:   { icon: Rocket,       color: "#94a3b8", label: "Direct",   sub: "Typed URL, bookmarks, no referrer" },
  Search:   { icon: SearchIcon,   color: "#60a5fa", label: "Search",   sub: "Google, Bing, DuckDuckGo, Yahoo" },
  Referral: { icon: ReferralIcon, color: "#34d399", label: "Referral", sub: "Any other website" },
};

function TrafficBucketBars({ buckets }) {
  if (!buckets || buckets.length === 0) {
    return <div className="text-sm text-slate-400 py-6 text-center">No traffic yet.</div>;
  }
  const max = Math.max(...buckets.map((b) => b.visitors), 1);
  return (
    <div className="space-y-3">
      {buckets.map((b) => {
        const style = BUCKET_STYLE[b.bucket] || BUCKET_STYLE.Referral;
        const Icon = style.icon;
        const width = Math.max(2, (b.visitors / max) * 100);
        return (
          <div key={b.bucket} data-testid={`bucket-${b.bucket.toLowerCase()}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <div className="flex items-center gap-2" style={{ color: style.color }}>
                <Icon className="h-4 w-4" />
                <span className="font-medium">{style.label}</span>
              </div>
              <div className="text-slate-500">
                <span className="font-medium text-slate-900">{b.visitors}</span>
                <span className="mx-1">·</span>
                <span>{b.percentage.toFixed(0)}%</span>
              </div>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${width}%`, background: style.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DevicesDonut({ devices }) {
  const total = (devices || []).reduce((s, d) => s + (d.visitors || 0), 0);
  if (!devices || devices.length === 0 || total === 0) {
    return <div className="h-64 flex items-center justify-center text-sm text-slate-400">No device data yet.</div>;
  }
  const COLORS = {
    mobile: "#a78bfa",
    desktop: "#60a5fa",
    tablet: "#34d399",
  };
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={devices}
            dataKey="visitors"
            nameKey="device"
            innerRadius={55}
            outerRadius={90}
            paddingAngle={2}
            stroke="none"
          >
            {devices.map((d) => (
              <Cell key={d.device} fill={COLORS[d.device] || "#cbd5e1"} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0", fontSize: 12 }}
            formatter={(v, n) => [`${v} visitors`, n]}
          />
          <Legend
            verticalAlign="bottom"
            height={30}
            formatter={(value, entry) => {
              const d = devices.find((x) => x.device === value);
              return (
                <span className="text-xs text-slate-600">
                  <span className="capitalize">{value}</span> · <span className="font-medium text-slate-900">{d?.visitors || 0}</span>
                </span>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function FunnelChart({ stages }) {
  const top = stages?.[0]?.count || 1;
  return (
    <div className="space-y-2">
      {stages.map((s, idx) => {
        const width = Math.max(2, (s.count / top) * 100);
        const isDropoffStage = idx > 0 && s.dropoff >= 90;
        return (
          <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
            <div className="w-56 text-sm text-slate-700">{s.label}</div>
            <div className="flex-1 relative h-8 bg-slate-100 rounded overflow-hidden">
              <div
                className={`h-full ${isDropoffStage ? "bg-rose-500" : "bg-blue-600"}`}
                style={{ width: `${width}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-medium text-white mix-blend-difference">
                {s.count} · {fmtPct(s.fromTop)}
              </div>
            </div>
            <div className={`w-20 text-right text-xs ${isDropoffStage ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
              {idx === 0 ? "—" : `${fmtPct(s.dropoff)} drop`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SimpleTable({ columns, rows, empty = "No data yet." }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-slate-400 py-6 text-center">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            {columns.map((c) => (
              <th key={c.key} className="py-2 pr-3 font-medium">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-none hover:bg-slate-50">
              {columns.map((c) => (
                <td key={c.key} className="py-2 pr-3 align-top">
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdSpendPanel({ spend, onAdd, onDelete }) {
  const [month, setMonth] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }).slice(0, 7));
  const [amount, setAmount] = useState("");
  const [channel, setChannel] = useState("total");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!amount || Number.isNaN(parseFloat(amount))) return;
    setSaving(true);
    try {
      await onAdd({ month, amount: parseFloat(amount), channel, notes });
      setAmount("");
      setNotes("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Ad Spend (manual entry)" testId="analytics-ad-spend">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4 items-end">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Month</label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="adspend-month"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Amount ($)</label>
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            data-testid="adspend-amount"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Channel</label>
          <Input value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="adspend-channel" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Notes</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="adspend-notes" />
        </div>
        <div>
          <Button
            onClick={handleAdd}
            disabled={saving || !amount}
            className="w-full"
            data-testid="adspend-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      <SimpleTable
        columns={[
          { key: "month", label: "Month" },
          { key: "channel", label: "Channel" },
          { key: "amount", label: "Amount", render: (r) => fmt$(r.amount) },
          { key: "notes", label: "Notes" },
          {
            key: "actions",
            label: "",
            render: (r) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(r.id)}
                data-testid={`adspend-del-${r.id}`}
              >
                <Trash2 className="h-4 w-4 text-slate-400" />
              </Button>
            ),
          },
        ]}
        rows={spend}
        empty="No ad-spend entered yet. Add the monthly total to see cost-per-signup."
      />
    </SectionCard>
  );
}

export default function AdminAnalytics() {
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [sources, setSources] = useState({ data: [], buckets: [] });
  const [utm, setUtm] = useState([]);
  const [pages, setPages] = useState({ landing: [], exits: [], views: [] });
  const [funnel, setFunnel] = useState([]);
  const [ctas, setCtas] = useState([]);
  const [devices, setDevices] = useState({ devices: [], browsers: [] });
  const [events, setEvents] = useState([]);
  const [spend, setSpend] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const params = { range };
      const [ov, tr, sr, u, pg, fn, ct, dv, ev, sp] = await Promise.all([
        fetchAdmin("overview", params),
        fetchAdmin("trend", params),
        fetchAdmin("sources", params),
        fetchAdmin("utm", params),
        fetchAdmin("pages", params),
        fetchAdmin("funnel", params),
        fetchAdmin("cta", params),
        fetchAdmin("devices", params),
        fetchAdmin("events", { ...params, limit: 100 }),
        fetchAdmin("adspend"),
      ]);
      setOverview(ov?.totals || null);
      setTrend((tr?.data || []).map((r) => ({ label: r.date, value: r.visitors })));
      setSources({ data: sr?.data || [], buckets: sr?.buckets || [] });
      setUtm(u?.data || []);
      setPages(pg || { landing: [], exits: [], views: [] });
      setFunnel(fn?.stages || []);
      setCtas(ct?.data || []);
      setDevices(dv || { devices: [], browsers: [] });
      setEvents(ev?.data || []);
      setSpend(sp?.data || []);
    } catch (e) {
      console.error("Analytics load error", e);
      setError(e?.response?.data?.detail || e.message || "Failed to load analytics.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const worstFunnelStage = useMemo(() => {
    if (!funnel || funnel.length < 2) return null;
    let worst = null;
    for (let i = 1; i < funnel.length; i++) {
      if (funnel[i - 1].count > 0 && (!worst || funnel[i].dropoff > worst.dropoff)) {
        worst = { ...funnel[i], prev: funnel[i - 1] };
      }
    }
    return worst;
  }, [funnel]);

  const handleAddSpend = async (payload) => {
    const token = localStorage.getItem("adminToken");
    await axios.post(`${BACKEND}/api/admin/analytics/adspend`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    load();
  };
  const handleDeleteSpend = async (id) => {
    const token = localStorage.getItem("adminToken");
    await axios.delete(`${BACKEND}/api/admin/analytics/adspend/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    load();
  };

  const handleExport = () => {
    const token = localStorage.getItem("adminToken");
    const url = `${BACKEND}/api/admin/analytics/export.csv?range=${range}`;
    // download with Auth header via fetch
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `landing_analytics_${range}.csv`;
        a.click();
      })
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const t = overview || {};
  const costPerSignup = t.signups > 0 ? t.adSpend / t.signups : null;

  return (
    <div className="space-y-4" data-testid="admin-analytics-root">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Range:</span>
          {RANGE_OPTIONS.map((r) => (
            <Button
              key={r.key}
              variant={range === r.key ? "default" : "outline"}
              size="sm"
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="analytics-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="analytics-export">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>
      ) : null}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiTile label="Visits" value={t.visits ?? 0} icon={Globe} tone="accent" testId="kpi-visits" />
        <KpiTile label="Unique Visitors" value={t.uniqueVisitors ?? 0} icon={Users} testId="kpi-visitors" />
        <KpiTile label="Bounce Rate" value={fmtPct(t.bounceRate)} icon={TrendingDown} tone={t.bounceRate > 60 ? "danger" : "default"} testId="kpi-bounce" />
        <KpiTile label="Avg. Time on Page" value={fmtMs(t.avgTimeOnPageMs)} testId="kpi-dwell" />
        <KpiTile label="Signups" value={t.signups ?? 0} tone="good" testId="kpi-signups" sub={t.activeSignups != null ? `${t.activeSignups} active` : null} />
        <KpiTile
          label="Ad Spend / Signup"
          value={costPerSignup == null ? fmt$(t.adSpend) : fmt$(costPerSignup)}
          sub={costPerSignup == null ? `${t.signups || 0} signups` : `${fmt$(t.adSpend)} spent · ${t.signups} signups`}
          icon={DollarSign}
          tone={costPerSignup && costPerSignup > 100 ? "warn" : "default"}
          testId="kpi-spend"
        />
      </div>

      {/* Drop-off narrative */}
      {worstFunnelStage ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="dropoff-callout">
          <span className="font-semibold">Biggest drop-off:</span>{" "}
          {fmtPct(worstFunnelStage.dropoff)} of visitors abandoned between{" "}
          <span className="font-medium">“{worstFunnelStage.prev.label}”</span> and{" "}
          <span className="font-medium">“{worstFunnelStage.label}”</span>
          {worstFunnelStage.key === "pricing"
            ? " — they never make it past your landing message."
            : worstFunnelStage.key === "signup_click"
            ? " — the pricing page isn't converting to a click."
            : worstFunnelStage.key === "trial_created"
            ? " — visitors click Sign Up but abandon the form."
            : ""}
        </div>
      ) : null}

      {/* Trend + Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="Views per day"
          right={<span className="text-xs text-slate-500">{RANGE_OPTIONS.find(r => r.key === range)?.label || range}</span>}
          testId="analytics-trend"
        >
          <ViewsLineChart data={trend} />
        </SectionCard>
        <SectionCard title="Conversion funnel" testId="analytics-funnel">
          {funnel.length ? <FunnelChart stages={funnel} /> : <div className="text-sm text-slate-400">Waiting for tracking data...</div>}
        </SectionCard>
      </div>

      {/* Traffic sources (bucketed) + UTM */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="Traffic sources"
          right={<span className="text-xs text-slate-500">Where visitors came from</span>}
          testId="analytics-referrers"
        >
          <TrafficBucketBars buckets={sources.buckets} />
          {sources.data && sources.data.length > 0 ? (
            <details className="mt-4">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                Show all referring domains ({sources.data.length})
              </summary>
              <div className="mt-2">
                <SimpleTable
                  columns={[
                    { key: "referrerDomain", label: "Domain", render: (r) => (
                      <div className="flex items-center gap-2">
                        <Globe className="h-3 w-3 text-slate-400" />
                        <span>{r.referrerDomain || "direct"}</span>
                      </div>
                    ) },
                    { key: "visitors", label: "Visitors" },
                    { key: "views", label: "Views" },
                    { key: "percentage", label: "%", render: (r) => fmtPct(r.percentage) },
                  ]}
                  rows={sources.data}
                />
              </div>
            </details>
          ) : null}
        </SectionCard>

        <SectionCard title="Top UTM campaigns" testId="analytics-utm">
          <SimpleTable
            columns={[
              { key: "campaign", label: "Campaign" },
              { key: "source", label: "Source" },
              { key: "medium", label: "Medium" },
              { key: "visitors", label: "Visitors" },
              { key: "views", label: "Views" },
            ]}
            rows={utm}
            empty="No UTM-tagged traffic yet. Add ?utm_source=… to your ad links."
          />
        </SectionCard>
      </div>

      {/* Landing / Exit pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Where visitors land" testId="analytics-landing">
          <SimpleTable
            columns={[
              { key: "path", label: "Path" },
              { key: "sessions", label: "Sessions" },
            ]}
            rows={pages.landing}
          />
        </SectionCard>
        <SectionCard title="Where visitors leave" testId="analytics-exits">
          <SimpleTable
            columns={[
              { key: "path", label: "Path" },
              { key: "sessions", label: "Exit sessions" },
            ]}
            rows={pages.exits}
          />
        </SectionCard>
      </div>

      {/* CTAs + Devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Top CTA clicks" testId="analytics-ctas">
          <SimpleTable
            columns={[
              { key: "cta", label: "CTA", render: (r) => (
                <div className="flex items-center gap-2">
                  <MousePointerClick className="h-3 w-3 text-slate-400" />
                  <span className="font-medium">{r.cta}</span>
                </div>
              ) },
              { key: "clicks", label: "Clicks" },
              { key: "sessions", label: "Sessions" },
            ]}
            rows={ctas}
            empty="Add data-analytics-cta on landing buttons to start capturing clicks."
          />
        </SectionCard>

        <SectionCard
          title="Devices"
          right={<span className="text-xs text-slate-500">Desktop vs mobile vs tablet</span>}
          testId="analytics-devices"
        >
          <DevicesDonut devices={devices.devices} />
          {devices.browsers && devices.browsers.length > 0 ? (
            <details className="mt-2">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                Show browser breakdown
              </summary>
              <div className="mt-2">
                <SimpleTable
                  columns={[
                    { key: "browser", label: "Browser" },
                    { key: "visitors", label: "Visitors" },
                  ]}
                  rows={devices.browsers}
                />
              </div>
            </details>
          ) : null}
        </SectionCard>
      </div>

      {/* Ad spend */}
      <AdSpendPanel spend={spend} onAdd={handleAddSpend} onDelete={handleDeleteSpend} />

      {/* Raw event log */}
      <SectionCard
        title="Recent pageview log"
        right={<Badge variant="secondary">{events.length} events</Badge>}
        testId="analytics-events-log"
      >
        <SimpleTable
          columns={[
            { key: "createdAt", label: "When (ET)", render: (r) => new Date(r.createdAt).toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) },
            { key: "path", label: "Page" },
            { key: "referrerDomain", label: "From" },
            { key: "utmCampaign", label: "Campaign", render: (r) => r.utmCampaign || "—" },
            { key: "device", label: "Device" },
            { key: "browser", label: "Browser" },
            { key: "practiceEmail", label: "Practice", render: (r) => r.practiceEmail || "anon" },
            { key: "dwellMs", label: "Dwell", render: (r) => r.dwellMs ? fmtMs(r.dwellMs) : "—" },
          ]}
          rows={events}
        />
      </SectionCard>

      {/* Empty-state helper */}
      {(t.visits ?? 0) === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          <div className="font-semibold text-slate-700 mb-1">No landing traffic captured yet in this range.</div>
          Analytics starts recording as soon as anyone visits your landing pages. Open{" "}
          <a href="/" className="text-blue-600 underline inline-flex items-center gap-1">
            the landing page <ExternalLink className="h-3 w-3" />
          </a>{" "}
          in a new tab to generate a test event, or wait for real visitors.
        </div>
      ) : null}
    </div>
  );
}
